import React from "react";
import { Box, Tooltip } from "@mui/material";
import ViewListRoundedIcon from "@mui/icons-material/ViewListRounded";
import GridViewRoundedIcon from "@mui/icons-material/GridViewRounded";
import ViewSidebarRoundedIcon from "@mui/icons-material/ViewSidebarRounded";
import { tokens } from "../../theme";

export type HubView = "list" | "grid" | "detail";

export interface ViewSegmentedProps {
  value: HubView;
  onChange: (v: HubView) => void;
  options?: HubView[];
}

const ICONS: Record<HubView, React.ReactNode> = {
  list: <ViewListRoundedIcon sx={{ fontSize: 18 }} />,
  grid: <GridViewRoundedIcon sx={{ fontSize: 18 }} />,
  detail: <ViewSidebarRoundedIcon sx={{ fontSize: 18 }} />,
};

const LABELS: Record<HubView, string> = {
  list: "List",
  grid: "Grid",
  detail: "Detail",
};

/**
 * Segmented List / Grid / Detail control. Mirrors `.viewseg` from ommuse-theme.css.
 */
export const ViewSegmented: React.FC<ViewSegmentedProps> = ({
  value,
  onChange,
  options = ["list", "grid", "detail"],
}) => (
  <Box
    sx={{
      display: "inline-flex",
      alignItems: "center",
      background: tokens.color.surface.raised,
      border: `1px solid ${tokens.color.border}`,
      borderRadius: `10px`,
      padding: "3px",
      gap: "3px",
      boxShadow: tokens.shadow.edgeHi,
    }}
  >
    {options.map((opt) => {
      const active = opt === value;
      return (
        <Tooltip key={opt} title={LABELS[opt]}>
          <Box
            component="button"
            type="button"
            aria-label={LABELS[opt]}
            aria-pressed={active}
            onClick={() => onChange(opt)}
            sx={{
              display: "grid",
              placeItems: "center",
              width: 32,
              height: 30,
              border: active ? "none" : `1px solid transparent`,
              borderRadius: `7px`,
              cursor: "pointer",
              color: active
                ? tokens.color.text.heading
                : tokens.color.text.secondary,
              background: active
                ? "linear-gradient(180deg, #242835, #191c24)"
                : "transparent",
              boxShadow: active
                ? `var(--edge-hi), 0 1px 3px rgba(0,0,0,.45)`
                : "none",
              transition: "all .15s cubic-bezier(0.16, 1, 0.3, 1)",
              "&:hover": {
                color: tokens.color.text.body,
                background: active
                  ? "linear-gradient(180deg, #242835, #191c24)"
                  : tokens.color.surface.raisedHi,
              },
              "&:active": {
                transform: "scale(0.96)",
              },
            }}
          >
            {ICONS[opt]}
          </Box>
        </Tooltip>
      );
    })}
  </Box>
);

export default ViewSegmented;
